import { FormControl, FormGroup, FormLabel, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { memo } from 'react';
import { CartModifierGroup } from '../../../utils/cart';
import {
  checkItemInCartItemTree,
  isDefaultItemInModGroup,
  isItem86edToday,
} from '../../../utils/menu';
import {
  getModGroupDisplayName,
  ModGroupLabel,
} from '../components/ModGroupLabel';
import MultipleModChoice from '../components/MultipleModChoice';
import RecursiveModGroup from '../menu/RecursiveModGroup';
import {
  getCartItemDescription,
  getModGroupItemLabel,
  sortAndFilterMenuItems,
} from '../util/helper';
import { IModGroup } from './modGroup.props';
import {
  canShowItem,
  hasModGroupChildren,
  EXPRESS_SUPPRESSED_LABEL_TOOLTIP,
} from './modGroup.utils';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import Colors from '../../../utils/color';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import {
  selectIsExpressUpsellSuppressionActive,
  selectUnavailableItemsByName,
} from '../../../selectors/menu';
import { isExpressSuppressedUpsellPrompt } from '../../../utils/cart.upsellConditions';
import { useSelector } from 'react-redux';

const useStyles = makeStyles<{
  hasChildren: boolean;
  isHighlighted: boolean;
  isLabelClickable: boolean;
  isLabelSuppressed: boolean;
}>()(
  (
    { spacing },
    { hasChildren, isHighlighted, isLabelClickable, isLabelSuppressed }
  ) => {
    const bgColor = isHighlighted ? Colors.alto : Colors.white;
    const mBottom = hasChildren ? spacing(1.25) : 0;
    // Express mode suppressed this heading: not-allowed rather than the default
    // arrow, so the label reads as deliberately inert instead of unresponsive.
    let labelCursor = 'default';
    if (isLabelClickable) {
      labelCursor = 'pointer';
    } else if (isLabelSuppressed) {
      labelCursor = 'not-allowed';
    }
    return {
      modGroup: {
        margin: '0 !important',
        '& legend': {
          width: '100%',
        },
        width: '100%',
        backgroundColor: bgColor,
      },
      modGroupContainer: {
        marginBottom: mBottom,
      },
      modGroupLabel: {
        cursor: labelCursor,
      },
    };
  }
);

const MultipleSelectionModGroup = (props: IModGroup) => {
  const {
    modGroup,
    onChange,
    cartItem,
    pathToItem,
    parentModifier,
    isHighlighted,
    isSelectedToFocus,
    sendModGroupTTSMessage,
    sendTTSMessage,
  } = props;
  const isBrandMenu = getMenuUnificationFeatureFlag();
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);

  //sort and filter the modifiers before rendering
  const menuItems = isBrandMenu
    ? Object.values(modGroup.menuItems)
    : sortAndFilterMenuItems(modGroup.menuItems);

  const currentItem = checkItemInCartItemTree({
    cartItem,
    pathToItem,
  }) as CartModifierGroup;
  let selectedModNumber = 0;
  if (currentItem) {
    const selectedMod = currentItem?.selectedItems || [];
    selectedModNumber = Object.values(selectedMod).length;
  }
  const key = [cartItem.id, modGroup.id].join('_');
  const hasChildren = hasModGroupChildren(props);
  const hasSpokenDescription = !!(isBrandMenu && modGroup.description);
  // Express mode must never ask a `do_not_upsell_in_express_mode`-tagged
  // upsell prompt, so stop advertising its heading as clickable (HITL-1529).
  // Mirrors the
  // rule enforced in useTTSMessage; a boolean selector re-renders the group
  // only when express mode itself flips, which happens at most once an order.
  // Scoped to headings that would otherwise have spoken: a group with nothing
  // to say was never clickable, so it has no suppression to explain.
  const isExpressUpsellSuppressed = useSelector(
    selectIsExpressUpsellSuppressionActive
  );
  const isLabelSuppressed =
    hasSpokenDescription &&
    isExpressSuppressedUpsellPrompt(modGroup, isExpressUpsellSuppressed);
  const isLabelClickable = hasSpokenDescription && !isLabelSuppressed;
  const { classes } = useStyles({
    hasChildren,
    isHighlighted: !!isHighlighted || !!isSelectedToFocus,
    isLabelClickable,
    isLabelSuppressed,
  });

  // Gated on the description alone, not on isLabelClickable: when express mode
  // has suppressed the prompt the click must still reach the hook, which drops
  // the TTS and records the attempt as a metric.
  const handleLabelClick = () => {
    if (hasSpokenDescription) {
      sendModGroupTTSMessage(modGroup);
    }
  };

  const modGroupHeading = (
    <div
      onClick={handleLabelClick}
      // The wrapper is a full-width block, so a tooltip anchored to it centres
      // on the whole row and drops over the option chips. Shrink the anchor to
      // the heading text while it is suppressed — the click target narrows with
      // it, which is fine here: the click is already a no-op that only records
      // the attempt, and a blank stretch of row should not record one.
      style={isLabelSuppressed ? { width: 'fit-content' } : undefined}
      // HITL-1532: Alt+click reads this heading out to the customer. The
      // heading is text rather than a control, so it opts in explicitly; and
      // its rendered text carries decorations meant for the agent — the
      // `[parent]` prefix, the `!` unmet marker, the `(select …)` counter — so
      // the group name is named as the part to speak. Symmetric with the item
      // header: a plain click sends the group's description, Alt+click says its
      // name.
      //
      // Both attributes drop while express mode has suppressed this prompt: the
      // heading of a `do_not_upsell_in_express_mode` group IS the upsell
      // question, and HITL-1529 exists so express mode never asks it — reading
      // it out through a second path would reopen exactly that. Unmarked, the
      // Alt+click falls through to handleLabelClick, which already drops the
      // TTS and records the attempt.
      data-speakable={isLabelSuppressed ? undefined : true}
      data-speak-text={
        isLabelSuppressed ? undefined : getModGroupDisplayName(modGroup)
      }
    >
      <FormLabel className={classes.modGroupLabel}>
        <ModGroupLabel
          modGroup={modGroup}
          selectedModNumber={selectedModNumber}
          parentModifier={parentModifier}
        />
      </FormLabel>
    </div>
  );

  return (
    <div key={key} className={classes.modGroupContainer}>
      <FormControl
        component="fieldset"
        className={classes.modGroup}
        data-testid="mod-group-form"
      >
        {isLabelSuppressed ? (
          <Tooltip
            title={EXPRESS_SUPPRESSED_LABEL_TOOLTIP}
            arrow
            placement="right"
          >
            {modGroupHeading}
          </Tooltip>
        ) : (
          modGroupHeading
        )}
        <FormGroup row>
          {menuItems.map((child, index) => {
            const is86edToday = isItem86edToday(child);
            const pathToModChoice = [pathToItem, child.id].join('__');
            const isSelected = !!checkItemInCartItemTree({
              cartItem,
              pathToItem: pathToModChoice,
            });
            const id = child.itemId;
            const name = child.name;
            const modLabel = getModGroupItemLabel(child);

            const isDefaultItem = isDefaultItemInModGroup(id, modGroup);

            const isItemUnavailableNow = isBrandMenu
              ? isBMModifierAnUnavailableConstraint(
                  cartItem,
                  unavailableItemsByName,
                  parentModifier,
                  pathToItem,
                  modGroup.name,
                  child.name
                )
              : false;

            const showItem = canShowItem({
              isBrandMenu,
              unavailableItemsByName,
              itemName: name,
              cartItem,
              modGroupName: modGroup.name,
              childAvailability: child?.available,
              is86edToday,
            });

            if (showItem) {
              const childDescription = getCartItemDescription(
                child,
                isBrandMenu
              );
              const onInfoClick = childDescription
                ? () => sendTTSMessage(childDescription)
                : undefined;
              return (
                <MultipleModChoice
                  key={pathToModChoice}
                  id={id}
                  name={name}
                  modLabel={modLabel}
                  onChange={onChange(modGroup)}
                  is86edToday={
                    isBrandMenu
                      ? !child.available || is86edToday || isItemUnavailableNow
                      : is86edToday
                  }
                  isSelected={isSelected}
                  isDefault={isDefaultItem}
                  onInfoClick={onInfoClick}
                  description={childDescription}
                />
              );
            }
            return null;
          })}
        </FormGroup>
      </FormControl>
      <RecursiveModGroup {...props} />
    </div>
  );
};

export default memo(MultipleSelectionModGroup);
